/**
 * Real analytics transport (Phase 2).
 *
 * Posts each flushed batch DIRECTLY to the stream endpoint — no parent-script
 * handoff. Envelope shape (EM-confirmed):
 *
 *   {
 *     streams: [{ stream: "pga_analytics", base, objects }],
 *     allBase: { source: "quiz-runtime", stream: "pga_analytics",
 *                sendType: "quiz", ...pageContext }
 *   }
 *
 * Delivery: navigator.sendBeacon with a text/plain Blob (a CORS "simple
 * request" — no preflight, and survives page unload). Falls back to a
 * keepalive fetch when sendBeacon is missing or refuses the payload.
 *
 * Implements AnalyticsTransport unchanged from Phase 1 — the engine is
 * unaware which transport it holds. Never throws into the caller; failures
 * are logged. The engine's bounded retry remains the safety net for any
 * unexpected synchronous throw.
 */

import { type PageContext, detectPageContext } from '@/analytics/page-context';
import type { AnalyticsBatch, AnalyticsTransport } from '@/analytics/types';

const DEFAULT_STREAM_URL = 'https://a3.pubguru.net/stream';
const STREAM_NAME = 'pga_analytics';
// text/plain keeps the request CORS-"simple" (no OPTIONS preflight). The body
// is still JSON — the stream must parse by content, not by Content-Type.
const CONTENT_TYPE = 'text/plain;charset=UTF-8';

function resolveStreamUrl(): string {
  const fromEnv = import.meta.env.VITE_ANALYTICS_STREAM_URL;
  return typeof fromEnv === 'string' && fromEnv.trim().length > 0 ? fromEnv : DEFAULT_STREAM_URL;
}

export class BeaconTransport implements AnalyticsTransport {
  private readonly url: string;
  // Page context is page-load-constant; resolve once per runtime instance.
  private readonly pageContext: PageContext;

  constructor(url: string = resolveStreamUrl(), pageContext: PageContext = detectPageContext()) {
    this.url = url;
    this.pageContext = pageContext;
  }

  send(batch: AnalyticsBatch): void {
    let body: string;
    try {
      body = JSON.stringify(this.envelope(batch));
    } catch (e) {
      console.warn('[quiz-runtime] analytics: failed to serialize batch', e);
      return;
    }
    if (!this.dispatch(body)) {
      console.warn('[quiz-runtime] analytics: no transport available to send batch');
    }
  }

  private envelope(batch: AnalyticsBatch): unknown {
    return {
      streams: [
        {
          stream: STREAM_NAME,
          base: batch.base,
          objects: batch.events,
        },
      ],
      allBase: {
        source: 'quiz-runtime',
        stream: STREAM_NAME,
        sendType: 'quiz',
        // Unresolved page-context fields were already omitted upstream.
        ...this.pageContext,
      },
    };
  }

  private dispatch(body: string): boolean {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: CONTENT_TYPE });
        if (navigator.sendBeacon(this.url, blob)) return true;
      }
    } catch {
      // sendBeacon can throw (e.g. payload over the UA limit) — fall through.
    }
    try {
      if (typeof fetch === 'function') {
        // keepalive lets the POST outlive an unloading page like a beacon.
        // no-cors + text/plain → simple request, no preflight; the opaque
        // response is fine for fire-and-forget analytics.
        void fetch(this.url, {
          method: 'POST',
          body,
          keepalive: true,
          mode: 'no-cors',
          headers: { 'Content-Type': CONTENT_TYPE },
        }).catch((e) => {
          console.warn('[quiz-runtime] analytics: fetch fallback failed', e);
        });
        return true;
      }
    } catch (e) {
      console.warn('[quiz-runtime] analytics: fetch fallback threw', e);
    }
    return false;
  }
}
